import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Delivery, DeliveryForZip, JsonDelivery } from '../bo/delivery';
import { environment } from '../../environments/environment';
import { Observable, shareReplay, tap } from 'rxjs';



const httpOptions = {
  headers: new HttpHeaders({ 'Content-Type': 'application/json' })
};


@Injectable({
  providedIn: 'root'
})


export class DeliveriesService {

  private readonly deliveriesBySiteCache = new Map<string, { expiresAt: number; stream: Observable<Delivery[]> }>();
  private readonly deliveriesBySiteTtlMs = 5 * 60 * 1000;


  constructor(private readonly http: HttpClient) { }



  public getDeliveriesBySiteId(siteId: number, dateStart: Date|null = null, dateEnd: Date|null = null) {
    const cacheKey = this.getDeliveriesBySiteCacheKey(siteId, dateStart, dateEnd);
    const cached = this.getCachedDeliveriesBySite(cacheKey);
    if (cached) {
      return cached;
    }

    const searchParams = new URLSearchParams();
    if (dateStart != null) searchParams.set('beginDate', dateStart.toISOString());
    if (dateEnd != null) searchParams.set('endDate', dateEnd.toISOString());

    const baseUrl = environment.exploreURL + '/deliveries/' + siteId;
    const urlWithQuery = `${baseUrl}?${searchParams.toString()}`;

    const request$ = this.http.get<Delivery[]>(urlWithQuery, httpOptions).pipe(
      tap({
        error: () => {
          this.deliveriesBySiteCache.delete(cacheKey);
        },
      }),
      shareReplay(1),
    );

    this.deliveriesBySiteCache.set(cacheKey, {
      expiresAt: Date.now() + this.deliveriesBySiteTtlMs,
      stream: request$,
    });

    return request$;
  };

  updateDelivery(deliveryId: string, blastId: number, delivery: any): Observable<any> {
    const baseUrl = environment.exploreURL + '/deliveries';
    return this.http.put<any>(
      `${baseUrl}/${deliveryId}?blastId=${blastId}`,
      delivery
    ).pipe(
      tap(() => this.clearDeliveriesBySiteCache()),
    );
  }

  public getDeliveriesByTirId(tirId: number) {
    return this.http.get(environment.exploreURL + '/deliveries/GetByTirId/' + tirId, httpOptions);
  }

  public getDeliveryByGuid(blastId: number, deliveryGuid: string): Observable<JsonDelivery> {
    return this.http.get<JsonDelivery>(environment.exploreURL + '/deliveries/' + blastId + '/deliveryLogJson/' + deliveryGuid, httpOptions);
  }

  public MoveDelivery(deliveryId: string, destinationBlastId: number): Observable<any> {
    return this.http.patch(environment.exploreURL + `/Deliveries/ReafectDeliveryToBlast?deliveryGuid=${deliveryId}&destBlastId=${destinationBlastId}`, httpOptions).pipe(
      tap(() => this.clearDeliveriesBySiteCache()),
    );
  }

  public SplitDelivery(deliveryId: string, newName: string, holeNames: string[]): Observable<any> {
    return this.http.post(environment.exploreURL + `/Deliveries/${deliveryId}/SplitDelivery?newName=${newName}`, holeNames, httpOptions).pipe(
      tap(() => this.clearDeliveriesBySiteCache()),
    );
  }

  public MergeDeliveries(sourceDeliveryId: string, destinationDeliveryId: string): Observable<any> {
    return this.http.post(environment.exploreURL + `/Deliveries/MergeDeliveries?sourceDeliveryId=${sourceDeliveryId}&destinationDeliveryId=${destinationDeliveryId}`, {}, httpOptions).pipe(
      tap(() => this.clearDeliveriesBySiteCache()),
    );
  }

  public getDeliveriesByBlastId(blastId: number): Observable<JsonDelivery[]> {
    return this.http.get<JsonDelivery[]>(environment.exploreURL + `/Deliveries/Blast/${blastId}`, httpOptions);
  }

  public clearDeliveriesBySiteCache(siteId?: number): void {
    if (siteId == null) {
      this.deliveriesBySiteCache.clear();
      return;
    }

    const prefix = `${siteId}|`;
    for (const key of this.deliveriesBySiteCache.keys()) {
      if (key.startsWith(prefix)) {
        this.deliveriesBySiteCache.delete(key);
      }
    }
  }

  private getDeliveriesBySiteCacheKey(siteId: number, dateStart: Date | null, dateEnd: Date | null): string {
    const start = dateStart?.toISOString() ?? '';
    const end = dateEnd?.toISOString() ?? '';
    return `${siteId}|${start}|${end}`;
  }

  private getCachedDeliveriesBySite(cacheKey: string): Observable<Delivery[]> | null {
    const cached = this.deliveriesBySiteCache.get(cacheKey);
    if (!cached) {
      return null;
    }

    if (cached.expiresAt <= Date.now()) {
      this.deliveriesBySiteCache.delete(cacheKey);
      return null;
    }

    return cached.stream;
  }


}

